import React, { useState, useEffect } from "react";
import { Prompt, useHistory, useParams } from "react-router-dom";
import makeStyles from "@material-ui/styles/makeStyles";
import Typography from "@material-ui/core/Typography";
import BackButton from "../../common/backButton";
import Button from "@material-ui/core/Button";
import LoadingIndicator from "../../common/loadingIndicator";
import { useEligibleParticipants, useEligibilityOverrides, useProgramEvent } from "../hooks";
import OverrideParticipantList, { OverrideType } from "./components/overrideParticipantList";
import RemoveParticipantsModal from "./components/removeParticipantsModal";
import AddParticipantsModal from "./components/addParticipantsModal";
import { toast } from "react-toastify";

const useStyles = makeStyles((theme) => ({
    formHeader: {
        display: "grid",
        gridTemplateColumns: "auto 150px",
        gridTemplateRows: "auto",
        gridTemplateAreas: `"name saveButton" "title ." "subtitle ."`,
        marginBottom: theme.spacing(3),
    },
    name: {
        gridArea: "name",
        textTransform: "uppercase",
    },
    title: {
        gridArea: "title",
        fontWeight: "bold",
    },
    saveButton: {
        gridArea: "saveButton",
        height: 50,
    },
    formBody: {
        paddingLeft: theme.spacing(0),
        paddingBottom: theme.spacing(5),
        display: "flex",
        flexDirection: "column",
        gap: theme.spacing(5),
    },
}));

const AddRemoveParticipants = ({ isActive, topUser }) => {
    const classes = useStyles();
    const { id } = useParams();
    const [dirty, setDirty] = useState(false);
    const { programEvent, loading: loadingProgramEvent } = useProgramEvent(id);
    const { eligibleParticipants, loading: loadingEligibleParticipants, setEligibleParticipants } = useEligibleParticipants(id);
    const {
        getEligibilityOverridesAsync,
        addedParticipants,
        removedParticipants,
        loading: loadingOverrides,
        setEligibilityOverrides,
        updateEligibilityOverridesAsync,
    } = useEligibilityOverrides(id);
    const [showAddParticipantsModal, setShowAddParticipantsModal] = useState(false);
    const [showRemoveParticipantsModal, setShowRemoveParticipantsModal] = useState(false);
    const history = useHistory();
    const [readOnly, setReadOnly] = useState(false);


    useEffect(() => {

        //console.log(topUser);
        //console.log(programEvent);

        if (programEvent != null) {
            if (topUser != null) {
                if (topUser.advProf.isAdmin == false && topUser.advProf.isStaff == false && topUser.advProf.isSupervisor == false && topUser.userId != programEvent.creatorUserId) {
                    setReadOnly(true);
                }
            }

        }



    }, [topUser, programEvent]);


    const handleAddedParticipants = (selectedParticipants) => {
        const addedParticipantsOverrides = selectedParticipants.reduce((total, selected) => {
            if (!selected?.id || addedParticipants.find((p) => p.userProfileId === selected?.id)) return total;
            return [
                ...total,
                {
                    userProfileId: selected.id,
                    participant: selected,
                    programEventId: id,
                    eligible: true,
                },
            ];
        }, []);
        setEligibilityOverrides([...removedParticipants, ...addedParticipants, ...addedParticipantsOverrides]);
        if (!dirty) setDirty(true);
    };

    const handleRemoveParticipants = (selectedParticipants) => {
        const removedParticipantsOverrides = selectedParticipants.reduce((total, selected) => {
            if (!selected?.id || removedParticipants.find((p) => p.userProfileId === selected?.id)) return total;
            return [
                ...total,
                {
                    userProfileId: selected.id,
                    participant: selected,
                    programEventId: id,
                    eligible: false,
                },
            ];
        }, []);
        setEligibilityOverrides([...removedParticipants, ...addedParticipants, ...removedParticipantsOverrides]);
        if (!dirty) setDirty(true);
    };

    const handleRemoveFromParticipantList = (dto) => {
        const newEligibilityOverrides = [...removedParticipants, ...addedParticipants].filter((participant) => participant.userProfileId !== dto.userProfileId);
        setEligibilityOverrides(newEligibilityOverrides);
        if (!dirty) setDirty(true);
    };

    const handleRemoveFromAddedParticipants = (dto) => {
        handleRemoveFromParticipantList(dto);
    };

    const handleRemoveFromRemovedParticipants = (dto) => {
        handleRemoveFromParticipantList(dto);
        setEligibleParticipants([...eligibleParticipants, dto.participant]);
    };

    const handleSave = async () => {
        try {
            await updateEligibilityOverridesAsync();
            toast.success("Changes saved successfully");
            if (dirty) setDirty(false);
            history.push(`/events/${isActive ? "active" : "inactive"}/event/${id}/eligible-participant-list`);
        } catch (ex) {
            await getEligibilityOverridesAsync();
        }
    };

    if (loadingProgramEvent || loadingEligibleParticipants || loadingOverrides) {
        return <LoadingIndicator loading />;
    }

    if (programEvent == null) {
        return null;
    }

    return (
        <div>
            <Prompt when={dirty} message={() => "You have unsaved changes. Are you sure you want to leave?||Are you sure?||Yes, Leave||No, Stay Here"} />
            <div className={classes.formHeader}>
                <div className={classes.name}>
                    <BackButton />
                    <Typography variant="h5" color="secondary">
                        {programEvent.name}
                    </Typography>
                </div>
                <Typography className={classes.title} variant="h6">
                    Invitation List - Add/Remove
                </Typography>
                <Button className={classes.saveButton} type="submit" variant="contained" disabled={readOnly}  color="primary" onClick={handleSave}>
                    Save
                </Button>
            </div>
            <div className={classes.formBody}>
                <OverrideParticipantList
                    type={OverrideType.Added}
                    title="Added Participants"
                    subtitle="Do not meet the Eligibility Criteria"
                    participants={addedParticipants}
                    onActionBtnClick={() => setShowAddParticipantsModal(true)}
                    onRemove={handleRemoveFromAddedParticipants}
                    disabled={readOnly }
                />
                <OverrideParticipantList
                    type={OverrideType.Removed}
                    status={programEvent?.status}
                    title="Removed Participants"
                    subtitle="Meet the Eligibility Criteria"
                    participants={removedParticipants}
                    onActionBtnClick={() => setShowRemoveParticipantsModal(true)}
                    onRemove={handleRemoveFromRemovedParticipants}
                    disabled={readOnly}
                />
            </div>
            <AddParticipantsModal
                open={showAddParticipantsModal}
                currentOverrides={addedParticipants}
                currentEligibleParticipants={eligibleParticipants}
                onClose={() => setShowAddParticipantsModal(false)}
                onSubmit={handleAddedParticipants}
            />
            <RemoveParticipantsModal
                open={showRemoveParticipantsModal}
                currentOverrides={[...addedParticipants, ...removedParticipants]}
                options={eligibleParticipants}
                onClose={() => setShowRemoveParticipantsModal(false)}
                onSubmit={handleRemoveParticipants}
                keepMounted
            />
        </div>
    );
};

export default AddRemoveParticipants;
